import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logout } from '../firebase/auth';
import { LogOut, User, Settings, Menu, X, Home } from 'lucide-react';
import toast from 'react-hot-toast';
import ProfileModal from './ProfileModal';
import { useTranslation } from '../hooks/useTranslation';

const Navbar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);

  // Extract first name from displayName
  const getFirstName = (displayName) => {
    if (!displayName) return null;
    return displayName.trim().split(' ')[0];
  };

  // Handle mobile menu toggle
  const toggleMobileMenu = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleClickOutside = (event) => {
      // Don't process if menu is not open
      if (!isMobileMenuOpen) return;
      
      // Don't process if clicking inside the menu or hamburger button
      if (
        menuRef.current?.contains(event.target) ||
        hamburgerRef.current?.contains(event.target)
      ) {
        return;
      }
      
      setIsMobileMenuOpen(false);
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      // Use a small delay to prevent immediate closing when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      toast.success(t('auth.logoutSuccess'));
      navigate('/');
    } else {
      toast.error(t('auth.logoutError'));
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-gray-200">
      <div className="container mx-auto px-2 sm:px-4">
        <div className="flex justify-between items-center h-14 sm:h-16 relative touch-manipulation">
          {/* Home Icon - Left side */}
          <button 
            onClick={() => {
              // Check if we're on home page and on mobile
              if (window.location.pathname === '/' && window.innerWidth < 640) {
                // Scroll to first snap point
                const snapContainer = document.querySelector('.snap-y');
                if (snapContainer) {
                  snapContainer.scrollTo({ top: 0, behavior: 'smooth' });
                }
              } else {
                // Navigate to home page
                navigate('/');
              }
            }}
            className="flex items-center space-x-2 flex-shrink-0 min-h-[44px] min-w-[44px] touch-manipulation"
          >
            <Home className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
            <span className="text-base sm:text-lg md:text-xl font-bold text-gray-900 hidden sm:block truncate">{t('home.headerTitle')}</span>
          </button>

          {/* Mobile Center Title */}
          <div className="md:hidden absolute left-1/2 transform -translate-x-1/2">
            <span className="text-xl font-bold text-gray-900">{t('home.headerTitle')}</span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-4 flex-shrink-0">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors"
                >
                  <User className="h-5 w-5" />
                  <span className="hidden lg:inline text-sm lg:text-base truncate max-w-24">{getFirstName(user?.displayName) || user?.email?.split('@')[0] || t('common.user')}</span>
                </Link>
                <button
                  onClick={() => setShowProfileModal(true)}
                  className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors whitespace-nowrap p-2 min-w-[60px] lg:min-w-[80px]"
                >
                  <Settings className="h-5 w-5" />
                  <span className="hidden lg:inline text-sm">{t('navigation.profile')}</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 text-gray-700 hover:text-red-600 transition-colors whitespace-nowrap p-2 min-w-[60px] lg:min-w-[80px]"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="hidden lg:inline text-sm">{t('navigation.logout')}</span>
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-2 flex-shrink-0">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary-600 transition-colors whitespace-nowrap text-sm px-2 py-1"
                >
                  {t('navigation.login')}
                </Link>
                <Link
                  to="/register"
                  className="btn-primary whitespace-nowrap text-sm px-3 py-2"
                >
                  {t('navigation.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              ref={hamburgerRef}
              onClick={(e) => {
                toggleMobileMenu(e);
              }}
              className="text-gray-700 hover:text-primary-600 active:text-primary-700 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 touch-target"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <div className={`transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-180' : ''}`}>
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 z-50 md:hidden bg-black bg-opacity-50" 
            style={{ zIndex: 9999 }}
            onClick={(e) => {
              // Only close if clicking directly on the overlay, not the menu
              if (e.target === e.currentTarget) {
                setIsMobileMenuOpen(false);
              }
            }}
          >
            {/* Slide-out Menu */}
            <div 
              ref={menuRef}
              className="fixed right-0 top-0 h-full w-72 max-w-[75vw] bg-white shadow-xl flex flex-col"
              style={{ zIndex: 10000 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-900">{t('navigation.menu')}</h2>
                <button
                  onClick={toggleMobileMenu}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Menu Content */}
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto" style={{ pointerEvents: 'auto' }}>
                  {/* User Info - Display only, completely non-interactive */}
                  {user && (
                    <div 
                      className="p-4 border-b border-gray-100 bg-gray-50 select-none"
                      style={{ 
                        pointerEvents: 'none',
                        cursor: 'default',
                        touchAction: 'none'
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{getFirstName(user?.displayName) || user?.email?.split('@')[0] || t('common.user')}</p>
                          <p className="text-sm text-gray-500">{user?.email}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation Items */}
                  <div 
                    className="p-4 space-y-2" 
                    style={{ 
                      pointerEvents: 'auto',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    {user ? (
                      <>
                        <Link
                          to="/dashboard"
                          className="flex items-center px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 group"
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            // Link will handle navigation, route change effect will also close menu as backup
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                              <User className="h-4 w-4" />
                            </div>
                            <span className="font-medium">{t('navigation.dashboard')}</span>
                          </div>
                        </Link>
                        
                        <button
                          onClick={() => {
                            setShowProfileModal(true);
                            toggleMobileMenu();
                          }}
                          className="w-full flex items-center px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 group"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                              <Settings className="h-4 w-4" />
                            </div>
                            <span className="font-medium">{t('navigation.profile')}</span>
                          </div>
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          className="flex items-center px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 group"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                              <User className="h-4 w-4" />
                            </div>
                            <span className="font-medium">{t('navigation.login')}</span>
                          </div>
                        </Link>
                        
                        <Link
                          to="/register"
                          className="flex items-center px-4 py-3 text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-all duration-200 group"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center group-hover:bg-primary-400 transition-colors">
                              <User className="h-4 w-4" />
                            </div>
                            <span className="font-medium">{t('navigation.register')}</span>
                          </div>
                        </Link>
                      </>
                    )}
                  </div>
                </div>

                {/* Logout Button (for logged in users) - Fixed at bottom */}
                {user && (
                  <div className="px-4 pt-4 pb-6 border-t border-gray-200 bg-white flex-shrink-0" style={{ pointerEvents: 'auto', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-4 text-red-600 hover:text-red-700 active:text-red-800 hover:bg-red-50 active:bg-red-100 rounded-lg transition-all duration-200 group touch-manipulation min-h-[56px]"
                    >
                      <div className="flex items-center space-x-3 w-full">
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 group-active:bg-red-300 transition-colors flex-shrink-0">
                          <LogOut className="h-4 w-4" />
                        </div>
                        <span className="font-medium">{t('navigation.logout')}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <ProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
      />
    </nav>
  );
};

export default Navbar;
